// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import * as yaml from 'yaml';
import { HierarchyNode, IDeserializable, ISerializable, LearningStore, SerializationContext } from '.';
import { assert, isObjectType, isString, MessageList } from '../utilities';

/** VS Code for Education object model serializer. */
export class Serializer<TLocation = string> {
    private static readonly _encoder = new TextEncoder();
    private static readonly _decoder = new TextDecoder('utf-8');

    /** List of callbacks to be called upon deserialization. */
    private readonly _onResolve: (() => void)[] = [];

    /** Returns messages reported during (de)serialization. */
    public readonly messages = new MessageList<TLocation>();

    /** Initializes a new instance of the Serializer class. */
    public constructor(
        /** Returns learning store. */
        public readonly store: LearningStore<TLocation>
    ) {}

    /**
     * @internal
     * Registers callback to be called at the end of store deserialization.
     **/
    public onResolve(callback: () => void): void {
        this._onResolve.push(callback);
    }

    /**
     * @internal
     * Invokes dependency resolution callbacks.
     **/
    public resolve() {
        for (const callback of this._onResolve) {
            callback();
        }
        this._onResolve.length = 0;
    }

    /**
     * Resolves location relative to specified root location.
     * @returns Resolved location.
     * @example: resolveLocation('intro-to-python/index.yaml', 'units/unit1.yaml') => 'intro-to-python/units/unit1.yaml'
     */
    protected resolveLocation(_root: TLocation | undefined, _relativePath: string): TLocation {
        assert.unreachable('This method must be overridden to support location.');
    }

    /** Loads content from specified location. */
    protected readContent(_location: TLocation): Promise<Uint8Array> {
        assert.unreachable('This method must be overridden to support location.');
    }

    /** Saves content to specified location. */
    protected writeContent(_location: TLocation, _buffer: Uint8Array): Promise<void> {
        assert.unreachable('This method must be overridden to support location.');
    }

    /**
     * Returns relative location string for a node.
     * @returns Relative location or undefined if node should be serialized inline.
     * @example getRelativeLocation('intro-to-python/index.yml', unit) => 'units/unit1.yaml'
     */
    protected getRelativeLocation(_root: TLocation | undefined, _node: ISerializable<TLocation>): string | undefined {
        return undefined;
    }

    /** Converts a buffer containing JSON or YAML content to object. */
    protected parse(buffer?: string | Uint8Array, location?: TLocation): { [name: string]: unknown } {
        const content = typeof buffer === 'string' ? buffer : Serializer._decoder.decode(buffer);
        const doc = yaml.parseDocument(content);
        this.messages.addRange(doc.errors, location, true);
        this.messages.addRange(doc.warnings, location);
        return doc.toJSON();
    }

    /** Converts an object to a buffer containing YAML content. */
    protected stringify(object?: object, prependSchema = true): string {
        const text = yaml.stringify(object);
        return prependSchema
            ? `# yaml-language-server: $schema=https://vscodeedu.com/schema/course-v2.schema.json\n${text}`
            : text;
    }

    /**
     * @internal
     * Serializes a node in specified parent context.
     * @returns Serialized object or string representing relative location.
     */
    public async serializeCore(
        context: SerializationContext<TLocation>,
        node: ISerializable<TLocation>
    ): Promise<string | object> {
        const relativeLocation = this.getRelativeLocation(context.location, node);
        if (relativeLocation !== undefined) {
            // Node should be serialized to a different location.
            const location = this.resolveLocation(context.location, relativeLocation);
            const nested = new SerializationContext(this, {}, undefined, location);
            await node.serialize(nested);
            const content = this.stringify(nested.object);
            const buffer = Serializer._encoder.encode(content);
            await this.writeContent(location, buffer);
            return relativeLocation;
        } else {
            // Node should be serialized inline.
            const nested = new SerializationContext(this, {}, undefined, context.location);
            await node.serialize(nested);
            return nested.object;
        }
    }

    /**
     * Serializes an object to a string.
     * @returns YAML representation of the serialized object.
     * @example const content = serializer.serialize(course);
     */
    public async serialize(node: ISerializable<TLocation>, prependSchema: boolean = false): Promise<string> {
        const context = new SerializationContext(this, {}, undefined, undefined);
        await node.serialize(context);
        return this.stringify(context.object, prependSchema);
    }

    /**
     * Serializes an object to specified location.
     * @example serializer.serializeTo(course, 'intro-to-python/index.yaml');
     */
    public async serializeTo(node: ISerializable<TLocation>, location: TLocation) {
        const context = new SerializationContext(this, {}, undefined, location);
        await node.serialize(context);
        const content = this.stringify(context.object);
        const buffer = Serializer._encoder.encode(content);
        await this.writeContent(location, buffer);
    }

    /**
     * @internal
     * Deserializes a node value in the context of a parent node.
     * @returns Deserialized node or undefined if deserialization failed.
     */
    public async deserializeCore<TNode>(
        type: IDeserializable<TLocation, TNode>,
        value: unknown,
        parent?: HierarchyNode<TLocation>
    ): Promise<TNode | undefined> {
        let object: { [name: string]: unknown };
        let location = parent?.location;

        if (isString(value)) {
            // Value is a relative location.
            try {
                location = this.resolveLocation(location, value);
                object = this.parse(await this.readContent(location), location);
            } catch (error) {
                this.messages.error(`Error occurred while loading content: ${error}.`, location);
                return undefined;
            }
        } else if (isObjectType(value)) {
            // Value is an object specified inline.
            object = value as { [name: string]: unknown };
        } else {
            this.messages.error(`Value to be deserialized must be a string or an object: '${value}'.`, location);
            return undefined;
        }

        const context = new SerializationContext(this, object, parent, location);
        return await type.create(context);
    }

    /**
     * Deserializes a node from a string and adds it to the store.
     * @returns Deserialized node or undefined if deserialization failed.
     * @example const course = serializer.deserialize(Course, content);
     */
    public async deserialize<TNode extends HierarchyNode<TLocation>>(
        type: IDeserializable<TLocation, TNode>,
        content: string | Uint8Array | object
    ): Promise<TNode | undefined> {
        const parse = typeof content === 'string' || content instanceof Uint8Array;
        const result = await this.deserializeCore(type, parse ? this.parse(content) : content);
        if (result !== undefined) {
            this.resolve();
            this.store.addNode(result);
            result.validate(this.messages, true);
        }
        return result;
    }

    /**
     * Deserializes a node from specified location and adds it to the store.
     * @returns Deserialized node or undefined if deserialization failed.
     * @example const course = serializer.deserialize(Course, 'intro-to-python/index.yaml');
     */
    public async deserializeFrom<TNode extends HierarchyNode<TLocation>>(
        type: IDeserializable<TLocation, TNode>,
        location: TLocation
    ): Promise<TNode | undefined> {
        const object = this.parse(await this.readContent(location), location);
        const context = new SerializationContext(this, object, undefined, location);
        const result = await type.create(context);
        if (result !== undefined) {
            this.resolve();
            this.store.addNode(result);
            result.validate(this.messages, true);
        }
        return result;
    }
}
